import { AuthProvider } from '@refinedev/core';
import axios from 'axios';
import { environment } from '@/config/environment';
import { appInsights } from '@/services/appInsights';
import {
  acquireMsalToken,
  InteractionRequiredAuthError,
  isMsalEnabled,
  logoutMsal,
  type MsalSessionData,
  startMsalLogin,
} from '@/services/msalAuth';

const SESSION_TABLE_UPDATED_KEY = 'is_table_updated';
const AUTHENTICATED_STORAGE_KEY = 'is_authenticated';
const SLOTD1_KEY = 'slotd1';
const SLOTD2_KEY = 'slotd2';
const SLOTD3_KEY = 'slotd3';
const SLOTD4_KEY = 'slotd4';
const SLOTD5_KEY = 'slotd5';
const SLOTD6_KEY = 'slotd6';
const USER_CONTEXT_STORAGE_KEY = 'otd_user_context';
const ACTIVE_BACKEND_SESSION_ID_KEY = 'otd_active_backend_session_id';
export const OTD_SESSION_ID_HEADER = 'X-OTD-Session-Id';
const BACKEND_SESSION_CACHE_TTL_MS = 10_000;

let authMeRequestPromise: Promise<AuthMeResponse | null> | null = null;
let refreshFlowPromise: Promise<OtdUserSession | null> | null = null;
let backendSessionInitializationPromise: Promise<void> | null = null;
let loginRedirectInProgress = false;
let encryptKey1 = '';
let encryptKey2 = '';
let encryptKey3 = '';
let currentSession: OtdUserSession | null = null;
let backendSessionLookupPromise: Promise<string> | null = null;
let cachedBackendSessionId: string | null = null;
let lastBackendSessionLookupAt = 0;
const derivedKeyCache = new Map<string, Promise<CryptoKey>>();
const derivedHashCache = new Map<string, Promise<string>>();
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');
const trimLeadingSlash = (value: string) => value.replace(/^\/+/, '');
const getStorageSalt = (purpose: string) => `${window.location.origin}:${trimTrailingSlash(environment.apiEndpoint) || 'api'}:${purpose}`;

const toApiUrl = (path: string) => {
  const normalizedApiEndpoint = trimTrailingSlash(environment.apiEndpoint);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (normalizedApiEndpoint.startsWith('/') && normalizedPath === normalizedApiEndpoint) {
    return normalizedPath;
  }

  if (normalizedApiEndpoint.startsWith('/') && normalizedPath.startsWith(`${normalizedApiEndpoint}/`)) {
    return normalizedPath;
  }

  return `${normalizedApiEndpoint}/${trimLeadingSlash(normalizedPath)}`;
};

const authDebugLog = (event: string, details?: Record<string, unknown>) => {
  if (!environment.authDebug) {
    return;
  }

  const payload = {
    at: new Date().toISOString(),
    event,
    ...(details ?? {}),
  };

  console.info('[auth-flow]', payload);
};

interface BackendSessionPayload {
  userId: string;
  sessionID: string;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';

  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });

  return window.btoa(binary);
}

function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes.buffer;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

async function deriveKeyMaterial(secret: string, purpose: string): Promise<CryptoKey> {
  const cacheKey = `${purpose}:${secret}`;
  const cached = derivedKeyCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const derived = (async () => {
    const keySeed = textEncoder.encode(`${getStorageSalt(purpose)}:${secret}`);
    const digest = await crypto.subtle.digest('SHA-256', keySeed);

    return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  })();

  derivedKeyCache.set(cacheKey, derived);

  return derived;
}

async function deriveStoredUserIdentifier(userId: string): Promise<string> {
  const cacheKey = `stored-user-identifier:${userId}`;
  const cached = derivedHashCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const derived = (async () => {
    const payload = textEncoder.encode(`${getStorageSalt('stored-user-identifier')}:${userId}`);
    const digest = await crypto.subtle.digest('SHA-256', payload);

    return bytesToHex(new Uint8Array(digest));
  })();

  derivedHashCache.set(cacheKey, derived);

  return derived;
}

async function encryptWithKey(text: string, key: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cryptoKey = await deriveKeyMaterial(key, 'local-storage-encryption');
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    textEncoder.encode(text),
  );

  return `${bytesToBase64(iv)}.${bytesToBase64(new Uint8Array(encrypted))}`;
}

async function decrypt(cipherText: string, key: string): Promise<string> {
  try {
    const [ivBase64, encryptedBase64] = cipherText.split('.');

    if (!ivBase64 || !encryptedBase64) {
      return '';
    }

    const cryptoKey = await deriveKeyMaterial(key, 'local-storage-encryption');
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBuffer(ivBase64) },
      cryptoKey,
      base64ToBuffer(encryptedBase64),
    );

    return textDecoder.decode(decrypted);
  } catch {
    return '';
  }
}

function isAuthenticated(): boolean {
  return localStorage.getItem(AUTHENTICATED_STORAGE_KEY) === 'true';
}

function isLocalhost(): boolean {
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

function isLocalMockAuthEnabled(): boolean {
  return isLocalhost() && environment.localAuth.enabled;
}

function hasSessionTableBeenUpdated(): boolean {
  return sessionStorage.getItem(SESSION_TABLE_UPDATED_KEY) === 'true';
}

function isBackendSessionReady(): boolean {
  return hasSessionTableBeenUpdated() && !!getStoredSessionMarker();
}

function markBackendSessionNotReady(): void {
  sessionStorage.setItem(SESSION_TABLE_UPDATED_KEY, 'false');
  setActiveBackendSessionId('');
  resetBackendSessionLookupCache();
}

function navigateToLogin(): void {
  if (isLocalMockAuthEnabled()) {
    authDebugLog('navigateToLogin.skipped', { reason: 'local_mock_auth_enabled' });
    return;
  }

  if (loginRedirectInProgress) {
    authDebugLog('navigateToLogin.skipped', { reason: 'redirect_in_progress' });
    return;
  }

  loginRedirectInProgress = true;

  if (isMsalEnabled()) {
    authDebugLog('navigateToLogin.redirect', { provider: 'msal' });
    void startMsalLogin().catch((error) => {
      loginRedirectInProgress = false;
      authDebugLog('navigateToLogin.error', {
        message: error instanceof Error ? error.message : 'unknown_error',
      });
    });
    return;
  }

  authDebugLog('navigateToLogin.skipped', { reason: 'msal_not_configured' });
  loginRedirectInProgress = false;
}

function buildAngularEncryptionKeys(token: string) {
  return {
    key1: token.substring(25, 50),
    key2: token.substring(0, 25),
    key3: token.substring(50, 75),
  };
}

async function saveAngularAuthInfo(accessToken: string, expiresOn: string, userId: string, email = ''): Promise<void> {
  const { key1, key2, key3 } = buildAngularEncryptionKeys(accessToken);
  encryptKey1 = key1;
  encryptKey2 = key2;
  encryptKey3 = key3;

  const [encryptedAccessToken, encryptedExpiration, encryptedUserId, storedUserIdentifier, encryptedEmail] = await Promise.all([
    encryptWithKey(accessToken, key1),
    encryptWithKey(expiresOn, key2),
    encryptWithKey(userId, key3),
    deriveStoredUserIdentifier(userId),
    encryptWithKey(email, key3),
  ]);

  localStorage.setItem(SLOTD1_KEY, encryptedAccessToken);
  localStorage.setItem(SLOTD2_KEY, encryptedExpiration);
  localStorage.setItem(SLOTD3_KEY, encryptedUserId);
  localStorage.setItem(SLOTD4_KEY, storedUserIdentifier);
  localStorage.setItem(SLOTD6_KEY, encryptedEmail);
  localStorage.setItem(AUTHENTICATED_STORAGE_KEY, 'true');
}

export async function getData(key: string, keyValue: string): Promise<string> {
  const raw = localStorage.getItem(key) ?? '';
  return raw ? decrypt(raw, keyValue) : '';
}

export async function getAuthorizationToken(): Promise<string> {
  const session = currentSession;

  if (session?.accessToken) {
    return session.accessToken;
  }

  return getData(SLOTD1_KEY, encryptKey1);
}

export async function getTokenExpiration(): Promise<string> {
  const session = currentSession;

  if (session?.expiresOn) {
    return session.expiresOn;
  }

  return getData(SLOTD2_KEY, encryptKey2);
}

export async function getUserId(): Promise<string> {
  const session = currentSession;

  if (session?.userId) {
    return session.userId;
  }

  return getData(SLOTD3_KEY, encryptKey3);
}

export async function getUserEmail(): Promise<string> {
  const session = currentSession;

  if (session?.email) {
    return session.email;
  }

  return getData(SLOTD6_KEY, encryptKey3);
}

function clearAuthStorage(): void {
  currentSession = null;
  backendSessionLookupPromise = null;
  cachedBackendSessionId = null;
  lastBackendSessionLookupAt = 0;
  encryptKey1 = '';
  encryptKey2 = '';
  encryptKey3 = '';
  derivedKeyCache.clear();
  derivedHashCache.clear();
  localStorage.removeItem(SLOTD1_KEY);
  localStorage.removeItem(SLOTD2_KEY);
  localStorage.removeItem(SLOTD3_KEY);
  localStorage.removeItem(SLOTD4_KEY);
  localStorage.removeItem(SLOTD5_KEY);
  localStorage.removeItem(SLOTD6_KEY);
  localStorage.removeItem(ACTIVE_BACKEND_SESSION_ID_KEY);
  localStorage.removeItem(AUTHENTICATED_STORAGE_KEY);
}

function getActiveBackendSessionId(): string {
  return localStorage.getItem(ACTIVE_BACKEND_SESSION_ID_KEY)?.trim() ?? '';
}

function setActiveBackendSessionId(sessionId: string): void {
  const normalizedSessionId = sessionId.trim();
  cachedBackendSessionId = normalizedSessionId;
  lastBackendSessionLookupAt = Date.now();

  if (!normalizedSessionId) {
    localStorage.removeItem(ACTIVE_BACKEND_SESSION_ID_KEY);
    return;
  }

  localStorage.setItem(ACTIVE_BACKEND_SESSION_ID_KEY, normalizedSessionId);
}

function buildSessionHeaders(accessToken?: string) {
  return {
    'Content-Type': 'application/json',
    ...(getActiveBackendSessionId() ? { [OTD_SESSION_ID_HEADER]: getActiveBackendSessionId() } : {}),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };
}

function resetBackendSessionLookupCache(): void {
  backendSessionLookupPromise = null;
  cachedBackendSessionId = null;
  lastBackendSessionLookupAt = 0;
}

function getCachedBackendSessionId(): string | null {
  if (cachedBackendSessionId === null) {
    return null;
  }

  if ((Date.now() - lastBackendSessionLookupAt) > BACKEND_SESSION_CACHE_TTL_MS) {
    return null;
  }

  return cachedBackendSessionId;
}

function toAuthMeResponse(data: MsalSessionData): AuthMeResponse {
  return {
    access_token: data.accessToken,
    expires_on: data.expiresOn,
    user_claims: data.claims,
    user_id: data.userId,
    email: claimValue(data.claims, ['email', 'preferred_username', 'upn', 'unique_name']) || data.userId,
    name: claimValue(data.claims, ['name']),
  };
}

function isRecoverableApiMeError(error: unknown): boolean {
  return axios.isAxiosError(error) && [401, 404].includes(error.response?.status ?? -1);
}

function isRecoverableSessionLookupError(error: unknown): boolean {
  return axios.isAxiosError(error) && [404].includes(error.response?.status ?? -1);
}

function getApiMeErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

function createSessionMarker(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return bytesToHex(crypto.getRandomValues(new Uint8Array(16)));
}

function getStoredSessionMarker(): string {
  return localStorage.getItem(SLOTD5_KEY) ?? '';
}

function buildBackendSessionPayload(session: Pick<OtdUserSession, 'userId'>, sessionMarker: string): BackendSessionPayload {
  return {
    userId: session.userId,
    sessionID: sessionMarker,
  };
}

async function fetchBackendSessionId(accessToken: string, options?: { force?: boolean }): Promise<string> {
  const shouldForce = options?.force === true;
  const cachedSessionId = shouldForce ? null : getCachedBackendSessionId();

  if (cachedSessionId !== null) {
    return cachedSessionId;
  }

  if (backendSessionLookupPromise) {
    return backendSessionLookupPromise;
  }

  backendSessionLookupPromise = (async () => {
    try {
      const { data } = await axios.get<string>(toApiUrl('/session'), {
        headers: buildSessionHeaders(accessToken),
      });

      const sessionId = typeof data === 'string' ? data.trim() : '';
      setActiveBackendSessionId(sessionId);

      return sessionId;
    } catch (error) {
      if (!isRecoverableSessionLookupError(error)) {
        throw error;
      }

      setActiveBackendSessionId('');
      authDebugLog('backendSession.lookupMissing', {
        status: getApiMeErrorStatus(error),
      });
      return '';
    } finally {
      backendSessionLookupPromise = null;
    }
  })();

  return backendSessionLookupPromise;
}

async function upsertBackendSession(accessToken: string, session: OtdUserSession, sessionMarker: string): Promise<void> {
  const existingSessionId = await fetchBackendSessionId(accessToken, { force: true });
  const payload = buildBackendSessionPayload(session, sessionMarker);

  if (existingSessionId) {
    await axios.put<number>(toApiUrl('/session'), payload, {
      headers: buildSessionHeaders(accessToken),
    });
    setActiveBackendSessionId(sessionMarker);
    authDebugLog('backendSession.updated', {
      userId: session.userId,
      sessionID: sessionMarker,
    });
    return;
  }

  await axios.post<number>(toApiUrl('/session'), payload, {
    headers: buildSessionHeaders(accessToken),
  });
  setActiveBackendSessionId(sessionMarker);
  authDebugLog('backendSession.inserted', {
    userId: session.userId,
    sessionID: sessionMarker,
  });
}

async function fetchApiMe(accessToken: string, fallbackData: MsalSessionData): Promise<AuthMeResponse> {
  try {
    const { data: meData } = await axios.get<Partial<AuthMeResponse>>(toApiUrl('/me'), {
      headers: buildSessionHeaders(accessToken),
    });

    return {
      access_token: meData.access_token ?? fallbackData.accessToken,
      expires_on: meData.expires_on ?? fallbackData.expiresOn,
      user_claims: Array.isArray(meData.user_claims) && meData.user_claims.length > 0 ? meData.user_claims : fallbackData.claims,
      user_id: meData.user_id ?? fallbackData.userId,
      email: typeof meData.email === 'string' ? meData.email : undefined,
      name: typeof meData.name === 'string' ? meData.name : undefined,
    };
  } catch (error) {
    if (!isRecoverableApiMeError(error)) {
      throw error;
    }

    authDebugLog('authMe.request.fallback', {
      status: getApiMeErrorStatus(error),
    });
    return toAuthMeResponse(fallbackData);
  }
}

async function ensureBackendSession(accessToken?: string): Promise<void> {
  if (!accessToken || isLocalMockAuthEnabled()) {
    return;
  }

  if (isBackendSessionReady()) {
    authDebugLog('backendSession.skip', { reason: 'already_validated_this_tab' });
    return;
  }

  if (backendSessionInitializationPromise) {
    authDebugLog('backendSession.reused');
    return backendSessionInitializationPromise;
  }

  backendSessionInitializationPromise = (async () => {
    const session = currentSession;
    if (!session?.userId) {
      authDebugLog('backendSession.skip', { reason: 'missing_user_id' });
      return;
    }

    const sessionMarker = getStoredSessionMarker() || createSessionMarker();

    await upsertBackendSession(accessToken, session, sessionMarker);

    localStorage.setItem(SLOTD5_KEY, sessionMarker);
    sessionStorage.setItem(SESSION_TABLE_UPDATED_KEY, 'true');
  })().finally(() => {
    backendSessionInitializationPromise = null;
  });

  return backendSessionInitializationPromise;
}

interface AuthMeResponse {
  access_token: string;
  expires_on: string;
  user_claims: Array<{ typ: string; val: string }>;
  user_id: string;
  email?: string;
  name?: string;
}

function createLocalMockToken(seed: string): string {
  const tokenSeed = `${seed}:${getStorageSalt('local-mock-token')}`;
  const repeatedToken = tokenSeed.repeat(Math.ceil(160 / tokenSeed.length));

  return repeatedToken.slice(0, 160);
}

function buildLocalMockAuthData(): AuthMeResponse {
  const userId = environment.localAuth.userId;
  return {
    access_token: createLocalMockToken(userId),
    expires_on: environment.localAuth.expiresOn,
    user_id: userId,
    email: userId,
    name: userId,
    user_claims: [
      { typ: 'preferred_username', val: userId },
      { typ: 'email', val: userId },
      { typ: 'name', val: userId },
      { typ: 'roles', val: environment.localAuth.role },
      { typ: 'region', val: environment.localAuth.region },
      { typ: 'product', val: environment.localAuth.product },
    ],
  };
}

function claimValue(claims: AuthMeResponse['user_claims'], types: string[]): string {
  const normalizedTypes = types.map((type) => type.toLowerCase());
  const exactMatch = claims.find((claim) => normalizedTypes.includes(claim.typ.toLowerCase()));
  if (exactMatch?.val) {
    return exactMatch.val;
  }

  const suffixMatch = claims.find((claim) => {
    const normalizedClaimType = claim.typ.toLowerCase();
    return normalizedTypes.some((type) => normalizedClaimType.endsWith(`/${type}`));
  });

  return suffixMatch?.val ?? '';
}

function deriveRole(claims: AuthMeResponse['user_claims']): string {
  return claimValue(claims, ['roles', 'role', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) || 'viewer';
}

function deriveRegion(claims: AuthMeResponse['user_claims']): string {
  return claimValue(claims, ['region', 'custom:region', 'extension_region', 'custom_region']) || '';
}

function deriveProduct(claims: AuthMeResponse['user_claims']): string {
  return claimValue(claims, ['product', 'custom:product', 'extension_product', 'custom_product']) || '';
}

function deriveEmail(data: AuthMeResponse): string {
  return String(data.email ?? '').trim() || claimValue(data.user_claims, [
    'email',
    'preferred_username',
    'upn',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn',
    'unique_name',
  ]) || (data.user_id.includes('@') ? data.user_id : '');
}

function deriveUserId(data: AuthMeResponse): string {
  return data.user_id
    || claimValue(data.user_claims, [
      'preferred_username',
      'upn',
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn',
      'email',
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
      'name',
    ])
    || '';
}

export interface OtdUserSession {
  userId: string;
  email: string;
  role: string;
  region: string;
  product: string;
  accessToken: string;
  expiresOn: string;
}

function buildSession(data: AuthMeResponse): OtdUserSession {
  return {
    userId: deriveUserId(data),
    email: deriveEmail(data),
    role: deriveRole(data.user_claims),
    region: deriveRegion(data.user_claims),
    product: deriveProduct(data.user_claims),
    accessToken: data.access_token,
    expiresOn: data.expires_on,
  };
}

async function hydrateSessionFromMsalData(data: MsalSessionData): Promise<OtdUserSession | null> {
  if (!data.accessToken) {
    return null;
  }

  const fallbackAuthData = toAuthMeResponse(data);
  const provisionalSession = buildSession(fallbackAuthData);

  await saveSession(provisionalSession, true);

  const authData = await fetchApiMe(data.accessToken, data);
  const hydratedSession = buildSession(authData);

  await saveSession(hydratedSession, false);

  authDebugLog('authMe.request.done', {
    hasAccessToken: !!authData.access_token,
    userId: authData.user_id ?? '',
    hasEmail: !!authData.email,
  });

  return hydratedSession;
}

async function persistSession(session: OtdUserSession): Promise<void> {
  currentSession = session;
  await saveAngularAuthInfo(session.accessToken, session.expiresOn, session.userId, session.email);
  loginRedirectInProgress = false;
}

async function saveSession(session: OtdUserSession, shouldSyncBackendSession = true): Promise<void> {
  await persistSession(session);
  if (shouldSyncBackendSession && !isLocalMockAuthEnabled()) {
    await ensureBackendSession(session.accessToken);
  }
}

async function fetchAuthMeData(): Promise<AuthMeResponse | null> {
  if (isLocalMockAuthEnabled()) {
    authDebugLog('authMe.mock', { userId: environment.localAuth.userId });
    return buildLocalMockAuthData();
  }

  if (authMeRequestPromise) {
    authDebugLog('authMe.reused');
    return authMeRequestPromise;
  }

  authDebugLog('authMe.request.start');
  authMeRequestPromise = acquireMsalToken(false)
    .then(async (data) => {
      if (!data) {
        return null;
      }

      const session = await hydrateSessionFromMsalData(data);
      return session
        ? {
          access_token: session.accessToken,
          expires_on: session.expiresOn,
          user_id: session.userId,
          email: session.email,
          user_claims: data.claims,
        }
        : null;
    })
    .catch((error) => {
      authDebugLog('authMe.request.error', {
        message: error instanceof Error ? error.message : 'unknown_error',
      });
      return null;
    })
    .finally(() => {
      authMeRequestPromise = null;
    });

  return authMeRequestPromise;
}

async function authenticate(): Promise<OtdUserSession | null> {
  sessionStorage.setItem(SESSION_TABLE_UPDATED_KEY, 'false');
  const authData = await fetchAuthMeData();
  if (!authData?.access_token) {
    return null;
  }

  const session = buildSession(authData);
  await saveSession(session);
  appInsights.logAuthentication(session.userId);
  return session;
}

export async function refreshTokenAndUpdateAuthData(): Promise<OtdUserSession | null> {
  if (refreshFlowPromise) {
    authDebugLog('refresh.reused');
    return refreshFlowPromise;
  }

  authDebugLog('refresh.start');
  refreshFlowPromise = (async () => {
    try {
      if (isLocalMockAuthEnabled()) {
        const localSession = buildSession(buildLocalMockAuthData());
        await saveSession(localSession, false);
        appInsights.logAuthentication(localSession.userId);
        authDebugLog('refresh.done', { userId: localSession.userId, source: 'local_mock' });
        return localSession;
      }

      const refreshedToken = await acquireMsalToken(true);
      authDebugLog('refresh.token.done');
      if (!refreshedToken) {
        appInsights.logTrace('Token refresh failed: missing access token.');
        authDebugLog('refresh.failed', { reason: 'missing_access_token' });
        return null;
      }

      const refreshed = await hydrateSessionFromMsalData(refreshedToken);
      if (!refreshed?.accessToken) {
        appInsights.logTrace('Token refresh failed: missing hydrated session.');
        authDebugLog('refresh.failed', { reason: 'missing_hydrated_session' });
        return null;
      }

      appInsights.logAuthentication(refreshed.userId);
      appInsights.logTrace('Token refresh succeeded.');
      authDebugLog('refresh.done', { userId: refreshed.userId });
      return refreshed;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        authDebugLog('refresh.interaction_required');
        navigateToLogin();
      }
      appInsights.logTrace('Token refresh failed.');
      authDebugLog('refresh.error', {
        message: error instanceof Error ? error.message : 'unknown_error',
      });
      return null;
    } finally {
      refreshFlowPromise = null;
    }
  })();

  return refreshFlowPromise;
}

export async function hasBackendSessionPermission(accessToken?: string): Promise<boolean | null> {
  if (!accessToken || isLocalMockAuthEnabled()) {
    return true;
  }

  if (!isBackendSessionReady()) {
    try {
      await ensureBackendSession(accessToken);
    } catch (error) {
      authDebugLog('backendSession.permissionCheck.skipped', {
        reason: 'initialization_failed',
        message: error instanceof Error ? error.message : 'unknown_error',
      });
      return null;
    }
  }

  const currentSessionMarker = getStoredSessionMarker();
  if (!hasSessionTableBeenUpdated() || !currentSessionMarker) {
    authDebugLog('backendSession.permissionCheck.skipped', {
      reason: 'not_ready',
      hasSessionTableUpdated: hasSessionTableBeenUpdated(),
      hasSessionMarker: !!currentSessionMarker,
    });
    return null;
  }

  let activeSessionId = await fetchBackendSessionId(accessToken);

  if (!activeSessionId || activeSessionId !== currentSessionMarker) {
    authDebugLog('backendSession.permissionCheck.repair.start', {
      reason: activeSessionId ? 'session_mismatch' : 'missing_remote_session',
      activeSessionId,
      currentSessionMarker,
    });

    try {
      markBackendSessionNotReady();
      await ensureBackendSession(accessToken);
      activeSessionId = await fetchBackendSessionId(accessToken, { force: true });
      authDebugLog('backendSession.permissionCheck.repair.done', {
        activeSessionId,
        currentSessionMarker,
      });
    } catch (error) {
      authDebugLog('backendSession.permissionCheck.repair.failed', {
        message: error instanceof Error ? error.message : 'unknown_error',
      });
      return null;
    }
  }

  return !!activeSessionId && activeSessionId === currentSessionMarker;
}

export function getCurrentSessionMarker(): string {
  return getStoredSessionMarker();
}

export function getBackendSessionHeaderValue(): string {
  return getActiveBackendSessionId();
}

function getStoredUserContext(): Pick<OtdUserSession, 'role' | 'region' | 'product'> {
  try {
    const raw = localStorage.getItem(USER_CONTEXT_STORAGE_KEY);
    if (!raw) {
      return { role: 'viewer', region: '', product: '' };
    }

    const parsed = JSON.parse(raw) as Partial<Record<'role' | 'region' | 'product', unknown>>;
    return {
      role: typeof parsed.role === 'string' && parsed.role.trim() ? parsed.role : 'viewer',
      region: typeof parsed.region === 'string' ? parsed.region : '',
      product: typeof parsed.product === 'string' ? parsed.product : '',
    };
  } catch {
    return { role: 'viewer', region: '', product: '' };
  }
}

async function resolveIdentity(): Promise<OtdUserSession | null> {
  if (currentSession) {
    return currentSession;
  }

  const [accessToken, expiresOn, userId, email] = await Promise.all([
    getAuthorizationToken(),
    getTokenExpiration(),
    getUserId(),
    getUserEmail(),
  ]);

  if (!accessToken || !userId) {
    return null;
  }

  const storedContext = getStoredUserContext();
  currentSession = {
    userId,
    email: email || (userId.includes('@') ? userId : ''),
    role: storedContext.role,
    region: storedContext.region,
    product: storedContext.product,
    accessToken,
    expiresOn,
  };

  return currentSession;
}

export const authProvider: AuthProvider = {
  login: async () => {
    authDebugLog('login.start');
    try {
      const session = await authenticate();
      if (!session?.accessToken) {
        authDebugLog('login.no_session');
        navigateToLogin();
        return { success: false, error: { name: 'Unauthorized', message: 'No session found.' } };
      }

      authDebugLog('login.success', { userId: session.userId });
      return { success: true, redirectTo: '/' };
    } catch (error) {
      authDebugLog('login.error', {
        message: error instanceof Error ? error.message : 'unknown_error',
      });
      navigateToLogin();
      return { success: false, error: { name: 'LoginError', message: 'Authentication failed.' } };
    }
  },

  logout: async () => {
    clearAuthStorage();
    resetBackendSessionLookupCache();
    sessionStorage.removeItem(SESSION_TABLE_UPDATED_KEY);
    appInsights.logLogout();
    if (isLocalMockAuthEnabled()) {
      loginRedirectInProgress = false;
      return { success: true, redirectTo: '/' };
    }
    if (isMsalEnabled()) {
      void logoutMsal();
    }
    return { success: true, redirectTo: '/' };
  },

  check: async () => {
    authDebugLog('check.start', { isAuthenticated: isAuthenticated() });

    if (!isAuthenticated()) {
      try {
        const session = await authenticate();
        if (session?.accessToken) {
          authDebugLog('check.pass', { source: 'authenticate' });
          return { authenticated: true };
        }
      } catch (error) {
        authDebugLog('check.error', {
          message: error instanceof Error ? error.message : 'unknown_error',
        });
      }

      navigateToLogin();
      return { authenticated: false };
    }

    const [accessToken, expiresOn, userId, email] = await Promise.all([
      getAuthorizationToken(),
      getTokenExpiration(),
      getUserId(),
      getUserEmail(),
    ]);

    if (!accessToken || !userId) {
      const refreshed = await refreshTokenAndUpdateAuthData();
      if (refreshed?.accessToken) {
        authDebugLog('check.pass', { source: 'refresh_missing_token' });
        return { authenticated: true };
      }

      authDebugLog('check.fail', { reason: 'missing_token_or_user' });
      navigateToLogin();
      return { authenticated: false };
    }

    try {
      const fallbackRole = currentSession?.role ?? 'viewer';
      const fallbackRegion = currentSession?.region ?? '';
      const fallbackProduct = currentSession?.product ?? '';
      const fallbackEmail = currentSession?.email ?? email ?? (userId.includes('@') ? userId : '');
      let session: OtdUserSession = currentSession ?? {
        userId,
        email: fallbackEmail,
        role: fallbackRole,
        region: fallbackRegion,
        product: fallbackProduct,
        accessToken,
        expiresOn,
      };

      const expiry = expiresOn === 'never' ? Number.POSITIVE_INFINITY : new Date(expiresOn).getTime();
      if (expiry < Date.now()) {
        const refreshed = await refreshTokenAndUpdateAuthData();
        if (!refreshed?.accessToken) {
          authDebugLog('check.fail', { reason: 'expired_and_refresh_failed' });
          clearAuthStorage();
          navigateToLogin();
          return { authenticated: false };
        }
        return { authenticated: true };
      }

      const hasStoredUserIdentifier = !!localStorage.getItem(SLOTD4_KEY);
      const hasStoredSessionMarker = !!localStorage.getItem(SLOTD5_KEY);
      const hasStoredEmail = !!localStorage.getItem(SLOTD6_KEY);

      if (!hasStoredUserIdentifier || !hasStoredSessionMarker || !hasStoredEmail) {
        await saveAngularAuthInfo(session.accessToken, session.expiresOn, session.userId, session.email);
        await ensureBackendSession(session.accessToken);

        if (!hasStoredSessionMarker || !session.email) {
          const hydratedAuthData = await fetchApiMe(session.accessToken, {
            accessToken: session.accessToken,
            expiresOn: session.expiresOn,
            userId: session.userId,
            claims: [],
          });

          session = buildSession(hydratedAuthData);
          await saveAngularAuthInfo(session.accessToken, session.expiresOn, session.userId, session.email);
        }
      }

      currentSession = session;
      authDebugLog('check.pass', { source: 'session_valid' });
      return { authenticated: true };
    } catch (error) {
      authDebugLog('check.error', {
        message: error instanceof Error ? error.message : 'unknown_error',
      });
      const refreshed = await refreshTokenAndUpdateAuthData();
      if (refreshed?.accessToken) {
        authDebugLog('check.pass', { source: 'catch_refresh' });
        return { authenticated: true };
      }

      authDebugLog('check.fail', { reason: 'catch_and_refresh_failed' });
      navigateToLogin();
      return { authenticated: false };
    }
  },

  getIdentity: async () => resolveIdentity(),

  getPermissions: async () => (await resolveIdentity())?.role ?? null,

  onError: async (error) => ({ error }),
};
